// services/payment.ts
import Stripe from 'stripe';
import { UserTier } from '../types';
import { PlanDetails } from '../types/subscription';

// Define subscription plans
// The price ID will be fetched from the server
export const SUBSCRIPTION_PLANS: Record<string, PlanDetails> = {
  alpha: {
    id: '', // This will be set dynamically from the server
    name: 'Alpha',
    price: 20,
    currency: 'USD',
    interval: 'year',
    features: [
      'No ads',
      'Faster response time',
      'No artificial delays',
      'The most thorough responses',
      'VIP support'
    ]
  }
};

// Default fallback price ID for development
const FALLBACK_ALPHA_PRICE_ID = 'price_1Qgv43EFMAcX4JyINId80fiV';

// Function to initialize subscription plans with server-provided price IDs
export async function initializeSubscriptionPlans(): Promise<void> {
  try {
    const apiUrl = import.meta.env.VITE_WORKER_URL || window.location.origin;
    const response = await fetch(`${apiUrl}/api/alpha-price-id`);
    
    if (!response.ok) {
      console.error('Failed to fetch Alpha price ID:', response.status);
      // Set fallback price ID
      SUBSCRIPTION_PLANS.alpha.id = FALLBACK_ALPHA_PRICE_ID;
      console.log('Using fallback Alpha price ID:', FALLBACK_ALPHA_PRICE_ID);
      return;
    }
    
    const priceId = await response.text();
    
    // Check if the response looks like HTML (indicating an error)
    if (priceId.trim().startsWith('<!DOCTYPE') || 
        priceId.trim().startsWith('<html') || 
        priceId.trim().startsWith('<?xml')) {
      console.error('Received HTML instead of price ID:', priceId.substring(0, 100) + '...');
      // Set fallback price ID
      SUBSCRIPTION_PLANS.alpha.id = FALLBACK_ALPHA_PRICE_ID;
      console.log('Using fallback Alpha price ID due to invalid response:', FALLBACK_ALPHA_PRICE_ID);
      return;
    }
    
    if (priceId) {
      SUBSCRIPTION_PLANS.alpha.id = priceId;
      console.log('Alpha price ID set to:', priceId);
    } else {
      // Set fallback price ID if empty response
      SUBSCRIPTION_PLANS.alpha.id = FALLBACK_ALPHA_PRICE_ID;
      console.log('Using fallback Alpha price ID due to empty response:', FALLBACK_ALPHA_PRICE_ID);
    }
  } catch (error) {
    console.error('Error initializing subscription plans:', error);
    // Set fallback price ID on error
    SUBSCRIPTION_PLANS.alpha.id = FALLBACK_ALPHA_PRICE_ID;
    console.log('Using fallback Alpha price ID due to error:', FALLBACK_ALPHA_PRICE_ID);
  }
}

// Define environment interface for Cloudflare Workers
export interface Env {
  USERS: KVNamespace;
  USER_ENCRYPTION_KEY: string;
  [key: string]: any;
}

// Define KVNamespace interface
interface KVNamespace {
  get(key: string, options?: any): Promise<string | null>;
  put(key: string, value: string, options?: any): Promise<void>;
  delete(key: string): Promise<void>;
}

// Helper function to get user by email
async function getUserByEmail(email: string, encryptionKey: string, env: Env): Promise<any> {
  // This is a placeholder for the actual implementation
  // In a real application, this would decrypt the email, hash it, and look up the user
  return null;
}

export interface PaymentProcessor {
  createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string>;
  handleWebhook(
    payload: any,
    signature: string,
    env: Env
  ): Promise<void>;
}

// Stripe Payment Processor
export class StripePaymentProcessor implements PaymentProcessor {
  private stripe: Stripe;
  private webhookSecret: string;

  constructor(apiKey: string, webhookSecret: string) {
    this.stripe = new Stripe(apiKey, {
      apiVersion: '2024-12-18.acacia' as any
    });
    this.webhookSecret = webhookSecret;
  }

  async createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    const session = await this.stripe.checkout.sessions.create({
      customer_email: userId,
      line_items: [{
        price: planId,
        quantity: 1
      }],
      mode: 'subscription',
      success_url: successUrl,
      cancel_url: cancelUrl,
      automatic_tax: { enabled: true },
      billing_address_collection: 'auto',
      customer_update: {
        address: 'never'
      }
    });

    return session.url || '';
  }

  async handleWebhook(payload: any, signature: string, env: Env): Promise<void> {
    try {
      const event = this.stripe.webhooks.constructEvent(
        payload,
        signature,
        this.webhookSecret
      );

      switch (event.type) {
        case 'checkout.session.completed':
          await this.handleCheckoutCompleted(event.data.object, env);
          break;
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdated(event.data.object, env);
          break;
        case 'customer.subscription.deleted':
          await this.handleSubscriptionCanceled(event.data.object, env);
          break;
      }
    } catch (err) {
      console.error('Webhook Error:', err);
      throw err;
    }
  }

  private async handleCheckoutCompleted(session: any, env: Env): Promise<void> {
    const userId = session.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    // Update user tier based on the subscription
    const subscription = await this.stripe.subscriptions.retrieve(session.subscription);
    const tier = this.getTierFromPrice(subscription.items.data[0].price.id);
    
    user.tier = tier;
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private async handleSubscriptionUpdated(subscription: any, env: Env): Promise<void> {
    const userId = subscription.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    const tier = this.getTierFromPrice(subscription.items.data[0].price.id);
    user.tier = tier;
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private async handleSubscriptionCanceled(subscription: any, env: Env): Promise<void> {
    const userId = subscription.customer_email;
    const user = await getUserByEmail(userId, env.USER_ENCRYPTION_KEY, env);
    
    if (!user) {
      throw new Error('User not found');
    }

    user.tier = 'free';
    await env.USERS.put(`user:${user.emailHash}`, JSON.stringify(user));
  }

  private getTierFromPrice(priceId: string): UserTier {
    // If the subscription plans have been initialized
    if (SUBSCRIPTION_PLANS.alpha.id && priceId === SUBSCRIPTION_PLANS.alpha.id) {
      return 'alpha';
    }
    return 'free';
  }
}

// Bitcoin Cash Payment Processor
export class BCHPaymentProcessor implements PaymentProcessor {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async createCheckoutSession(
    userId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    // Implement BCH payment flow
    // This would typically involve:
    // 1. Generating a unique BCH address for the payment
    // 2. Creating an order in your system
    // 3. Returning a URL to your BCH payment page
    
    const plan = SUBSCRIPTION_PLANS[planId];
    if (!plan) {
      throw new Error('Invalid plan');
    }

    // Generate a unique order ID
    const orderId = `order_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

    // Here you would integrate with your BCH payment provider
    // This is a placeholder for the actual implementation
    return `/bch-payment?orderId=${orderId}&amount=${plan.price}&userId=${userId}`;
  }

  async handleWebhook(payload: any, signature: string, env: Env): Promise<void> {
    // Implement BCH webhook handling
    // This would typically involve:
    // 1. Verifying the payment was received
    // 2. Updating the user's subscription status
    // 3. Handling subscription renewals
  }
}
